import { useState } from "react";
import RegionForm from "../../../components/forms/configuration/RegionForm";
import { Accordion, Alert, Button, Card, Col, Form, FormGroup, Image, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import BodyService from "../../../services/body.service";
import { useEffect } from "react";
import RegionService from "../../../services/region.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import UseAlertConfirm from "../../../hooks/UseAlertConfirm";
import PaginationComponent from "../../../components/PaginationComponent";
import TableHeaderSortComponent from "../../../components/TableHeaderSortComponent";
import APP_CONF from "../../../scripts/constants";
import { useForm } from "react-hook-form";
import Select from "react-select";
import ProvinceService from "../../../services/province.service";
import UserPermissionService from "../../../services/userPermission.service";

const Region = () => {

    const { t } = useTranslation();

    const [translations, setTranslations] = useState([]);

    const [regions, setRegions] = useState([]);

    const [province, setProvince] = useState();

    const [provinces, setProvinces] = useState([]);

    const [permissions, setPermissions] = useState({});

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: APP_CONF.ITEMS_PER_PAGE,
        numPages: 1
    });

    const { showModal } = UseAlertConfirm();

    const [success, setSuccess] = useState({
        create: false,
        delete: false,
    });

    const [sortColumn, setSortColumn] = useState({
        sortByColumn: null,
        orderBy: null
    });

    const [errors, setErrors] = useState({
        create: false,
        delete: false,
    });

    const { register, handleSubmit } = useForm({
        mode: "all",
    });

    const [filter, setFilter] = useState({
        active: "",
        search: ""
    })

    const createRegion = async (data) => {
        setErrors({ ...errors, create: false });
        setSuccess({ ...success, create: false });
        try {
            await RegionService.create(data);
            getEmptyTranslations();
            setSuccess({ ...success, create: true });
            getRegions();
        } catch (error) {
            console.error(error.response);
            setErrors({ ...errors, create: true });
        }
    };

    const getEmptyTranslations = async () => {
        const data = await BodyService.getEmptyTranslations();
        setTranslations(data);
    };

    const getRegions = async () => {
        try {
            const data = await RegionService.getAll(pagination, filter, sortColumn);
            setRegions(data.content);
            setPagination({
                ...pagination,
                totalItems: data.totalElements,
                numPages: data.totalPages
            });
        } catch (response) {
            if (response?.data?.errorCode) {
                switch (response.data.errorCode) {
                    case 13002:
                        setErrors({ ...errors, email: true });
                        break;
                    default:
                        setErrors({ ...errors, create: true });
                }
            }
        }
    }

    const deleteRegion = async (id) => {
        showModal({
            title: t("general.delete.title"),
            body: t("general.delete.message"),
            confirmButtonVariant: "danger",
            onConfirm: async () => {
                setErrors({ ...errors, create: false });
                setSuccess({ ...success, create: false });
                try {
                    await RegionService.remove(id);
                    setSuccess({ ...success, delete: true });
                    getRegions();
                } catch (e) {
                    console.error(e.response);
                    setErrors({ ...errors, delete: true });
                }
            }
        });
    }

    const filterSearch = (data) => {        
        setFilter({
            active: data.active,
            search: data.search,
            provinceId: province?.id
        });
    }

    const getProvinces = async () => {
        try {
            let emptyProvince = {id:null,name:{"es":{value:""}}};
            const data = await ProvinceService.getList();           
            setProvinces([emptyProvince,...data]);
        } catch (error) {
            console.error(error);
        }
    }

    const getPermissions = async () => {
        try {
            const data = await UserPermissionService.getPermissionsByConnected();
            setPermissions(data);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const loadData = async () => {
            await getPermissions(); 
            await getEmptyTranslations();
        }

        loadData();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            await Promise.all([getRegions(), getProvinces()]);
        }

        loadData();
    }, [pagination.currentPage, sortColumn, filter]);

    return (
        <>
            <div className={"d-sm-flex align-items-center justify-content-between mb-4"}>
                <h1 className={"h3 mb-0 text-gray-800"}>{t('region.title')}</h1>
            </div>

            <Alert variant={"danger"} show={errors.create} dismissible onClose={() => setErrors({ ...errors, create: false })}>
                {t('region.error.create')}
            </Alert>

            <Alert variant={"success"} show={success.create} dismissible onClose={() => setSuccess({ ...success, create: false })}>
                {t('region.success.create')}
            </Alert>

            <Alert variant={"success"} show={success.delete} dismissible onClose={() => setSuccess({ ...success, delete: false })}>
                {t('region.success.delete')}
            </Alert>

            <Alert variant={"danger"} show={errors.delete} dismissible onClose={() => setErrors({ ...errors, delete: false })}>
                {t('region.error.delete')}
            </Alert>
            {permissions.regions?.read && permissions.regions?.create ? 
            <Card className={"shadow mb-4"}>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><h6 className={"m-0 fw-bold text-primary"}>{t('region.form.title')}</h6>
                        </Accordion.Header>
                        <Accordion.Body>
                            <RegionForm onSubmit={createRegion} translations={translations} permissions={permissions} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>
            : null }

            <hr />
            <Card className="{shadow mb-4}">
                <Card.Header className={"py-3"}>
                    <h6 className={"m-0 fw-bold text-primary"}>{t('region.table.title')}</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(filterSearch)}>
                        <Row>
                            <Col lg="2" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('region.filter.active.title')}</Form.Label>
                                    <Form.Select {...register('active')} aria-label="Default select example">
                                        <option value="">{t('region.filter.active.value1')}</option>
                                        <option value={true}>{t('region.filter.active.value2')}</option>
                                        <option value={false}>{t('region.filter.active.value3')}</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col lg="3" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('region.filter.search')}</Form.Label>
                                    <Form.Control type="text" {...register("search")} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Form.Label>{t('region.filter.province')}</Form.Label>
                                    <Select options={provinces}
                                        classNamePrefix="select"
                                        placeholder=""
                                        getOptionLabel={(option) => option.name["es"].value}
                                        getOptionValue={(option) => option.id}
                                        onChange={(data) => {
                                            setProvince(data)
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button type="submit" className={"mt-3"} disabled={!permissions.regions?.read}>{t('region.filter.button')}</Button>
                    </Form>
                    <hr />
                    <Table responsive>
                        <thead>
                            <tr>
                                <TableHeaderSortComponent scope={"col"} text={t('region.table.name')} column={"name"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <TableHeaderSortComponent scope={"col"} text={t('region.table.province')} column={"province"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <TableHeaderSortComponent scope={"col"} text={t('region.table.active')} column={"active"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                regions ? regions.map((region, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex flex-row flex-nowrap">
                                                {
                                                    Object.values(region.name).map((translation, i) =>
                                                        <div key={i} className="me-2">
                                                            <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                            {i + 1 === Object.values(region.name).length ? translation.value : translation.value + ' | '}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </td>
                                        <td>{region?.province?.name["es"].value}</td>
                                        <td>{region?.active ? 'Sí' : 'No'}</td>
                                        <td className={"text-center"}>
                                            <Button variant="link" size="sm" className="ms-2" to={`/configuration/region/${region.id}`} as={Link} disabled={!permissions.regions?.read}>
                                                <FontAwesomeIcon icon="edit" />
                                            </Button>
                                            <Button variant="link" size="sm" className="text-danger" onClick={() => deleteRegion(region.id)} disabled={!permissions.regions?.delete}>
                                                <FontAwesomeIcon icon="trash" />
                                            </Button>
                                        </td>
                                    </tr>
                                )) :
                                    <tr>
                                        <td colSpan={4}>{t('general.empty')}</td>
                                    </tr>
                            }
                        </tbody>
                    </Table>
                    <PaginationComponent pagination={pagination}
                        setPagination={setPagination}
                        alwaysShown={regions.length > 0} />
                </Card.Body>
            </Card>
        </>
    );
};

export default Region;